import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  Conversa,
  ListarConversasResponse,
  FecharConversaResponse,
  TransferirConversaRequest,
  TransferirConversaResponse,
  ListarChatsResponse,
} from '../models/conversa.model';
import { ToastService } from 'src/app/shared/toasts/toast-service';
import { catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ConversaService {
  private apiUrl = `${environment.apiUrl}/v1/WhatsAppConversas`;

  constructor(
    private http: HttpClient,
    private toastService: ToastService
  ) {}

  listarConversas(filtros?: {
    instanciaId?: string;
    status?: string;
    atendenteId?: string;
  }): Observable<ListarConversasResponse> {
    let params = new HttpParams();
    if (filtros?.instanciaId) {
      params = params.set('instanciaId', filtros.instanciaId);
    }
    if (filtros?.status) {
      params = params.set('status', filtros.status);
    }
    if (filtros?.atendenteId) {
      params = params.set('atendenteId', filtros.atendenteId);
    }

    console.log('ConversaService: Listando conversas', { url: this.apiUrl, filtros, params: params.toString() });

    return this.http.get<ListarConversasResponse>(this.apiUrl, { params }).pipe(
      catchError((error) => {
        console.error('ConversaService: Erro ao listar conversas', {
          status: error.status,
          statusText: error.statusText,
          message: error.message,
          url: this.apiUrl,
          filtros
        });
        
        if (error.status === 403) {
          this.toastService.show('Acesso negado. Verifique suas permissões para acessar conversas.', {
            classname: 'bg-warning text-light',
          });
        } else {
          this.toastService.show('Erro ao listar conversas', {
            classname: 'bg-danger text-light',
          });
        }
        
        return throwError(() => error);
      })
    );
  }

  fecharConversa(conversaId: string): Observable<FecharConversaResponse> {
    return this.http
      .put<FecharConversaResponse>(`${this.apiUrl}/${conversaId}/fechar`, {})
      .pipe(
        catchError((error) => {
          this.toastService.show('Erro ao fechar conversa', {
            classname: 'bg-danger text-light',
          });
          return throwError(() => error);
        })
      );
  }

  transferirConversa(
    conversaId: string,
    atendenteDestinoId: string,
    motivo?: string
  ): Observable<TransferirConversaResponse> {
    const request: TransferirConversaRequest = {
      conversaId,
      atendenteDestinoId,
      motivo,
    };
    // URL correta: POST /api/v1/WhatsAppConversas/{conversaId}/transferir
    return this.http
      .post<TransferirConversaResponse>(`${this.apiUrl}/${conversaId}/transferir`, request)
      .pipe(
        catchError((error) => {
          // Tratar erros específicos da API
          if (error.error?.errors) {
            const errors = error.error.errors;
            let errorMessage = 'Erro ao transferir conversa';
            
            // Verificar se há mensagens de erro específicas
            if (errors[''] && Array.isArray(errors[''])) {
              errorMessage = errors[''][0];
            } else if (typeof errors === 'object') {
              const firstError = Object.values(errors)[0];
              if (Array.isArray(firstError) && firstError.length > 0) {
                errorMessage = firstError[0];
              }
            }
            
            this.toastService.show(errorMessage, {
              classname: 'bg-danger text-light',
            });
          } else {
            this.toastService.show('Erro ao transferir conversa', {
              classname: 'bg-danger text-light',
            });
          }
          return throwError(() => error);
        })
      );
  }

  /**
   * Listar chats de uma instância WhatsApp com paginação
   * @param limit Limite de itens por página
   * @param offset Offset para paginação
   * @param unreadOnly Filtrar apenas não lidas
   */
  listarChats(
    limit: number = 20,
    offset: number = 0,
    unreadOnly: boolean = false
  ): Observable<ListarChatsResponse> {
    const baseUrl = `${environment.apiUrl}/v1/WhatsAppInstancias/instances/chats`;
    let params = new HttpParams()
      .set('Limit', limit.toString())
      .set('Offset', offset.toString())
      .set('UnreadOnly', unreadOnly ? 'true' : 'false');

    return this.http.get<ListarChatsResponse>(baseUrl, { params }).pipe(
      catchError((error) => {
        console.error('ConversaService: Erro ao listar chats', {
          status: error.status,
          statusText: error.statusText,
          message: error.message,
          limit,
          offset,
          unreadOnly
        });
        
        this.toastService.show('Erro ao listar chats', {
          classname: 'bg-danger text-light',
        });
        
        return throwError(() => error);
      })
    );
  }
}
